// ═══════════════════════════════════════════════════════════════════
//  TOQUM · SUPPLIER AUDIT & CAP
//  Audit engine ported in full from Nirvikalp Sourcing.js:
//  9 sections · 10/5/3/0/N-A scoring · critical-question auto-fail ·
//  section comments · running score · A/B/C grading.
//  CAP page: raise from audit findings → supplier response →
//  verification → manager approval → closure, with progress tracking.
// ═══════════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import {
  T, uid, today, addDays, num, Section, Field, Grid, Btn, DataTable, Tag, Badge,
  StatCard, SCORE_COLORS, DynamicTable, Collapse, printHTML, isManager, isSuperAdmin,
} from './Shared';

// ── AUDIT CHECKLIST (verbatim from Sourcing.js) ──────────────────
export const AUDIT_SECTIONS = [
  { key:'lab', title:'Section 1: On-Site Laboratory', questions:[
    { id:'l1', text:'Is there an on-site, fully equipped laboratory with trained technician?', critical:false },
    { id:'l2', text:'Is your laboratory certified by major international testing bodies?', critical:false },
    { id:'l3', text:'Are critical laboratory tests performed (in-house or 3rd party) and results used for prevention?', critical:false },
    { id:'l4', text:'Are consistent fabric/garment shrinkage/wash tests conducted and properly evaluated?', critical:false },
  ]},
  { key:'sample', title:'Section 2: Sample Room, Pattern & Marker Making', questions:[
    { id:'s1', text:'Is the sample room fully equipped to produce representative samples?', critical:false },
    { id:'s2', text:'Is there a skilled sample room technician capable of pattern modification with handover records?', critical:true },
    { id:'s3', text:'Are CAD system and plotter used for grading, consumption and marker making?', critical:false },
    { id:'s4', text:'Are bulk patterns and markers checked for accuracy?', critical:false },
    { id:'s5', text:'Are shrinkage tests conducted for each bulk lot received?', critical:false },
    { id:'s6', text:'Are sample room fabric, trims & accessories well organized? Is housekeeping acceptable?', critical:false },
    { id:'s7', text:'Is lighting adequate (100 FC and above)?', critical:false },
  ]},
  { key:'warehouse', title:'Section 3: Warehousing of Fabrics, Yarns, Trims & Accessories', questions:[
    { id:'w1', text:'Are all fabrics/yarns/trims stored indoors, off floors, covered and properly organized?', critical:true },
    { id:'w2', text:'Are inspection processes appropriate for bulk fabrics/yarns with trained inspectors?', critical:false },
    { id:'w3', text:'Are inspection processes appropriate for bulk trims/accessories with trained inspectors?', critical:false },
    { id:'w4', text:'Is light box and approved shade band available for bulk color verification?', critical:false },
    { id:'w5', text:'Is lighting adequate for all inspection areas (100 FC and above)?', critical:false },
  ]},
  { key:'cutting', title:'Section 4: Spreading, Cutting & Fusing', questions:[
    { id:'c1', text:'Is there adequate WIP balance between cutting and production output?', critical:false },
    { id:'c2', text:'Are acceptable processes in place for adequate fabric relaxation?', critical:false },
    { id:'c3', text:'Are shade variances controlled within the spreading process?', critical:false },
    { id:'c4', text:'Is spreading methodology acceptable?', critical:false },
    { id:'c5', text:'Is there proper control for matching/cutting of patterned fabrics?', critical:false },
    { id:'c6', text:'Does factory have adequate storage for bulk cut inventory?', critical:false },
    { id:'c7', text:'Are cut pieces properly inspected and numbered?', critical:false },
    { id:'c8', text:'Is fusing & heat seal quality acceptable with machines calibrated and records maintained?', critical:false },
    { id:'c9', text:'Are bulk cut goods transported in an organized and protected manner with records?', critical:false },
    { id:'c10', text:'Is lighting adequate (100 FC and above)?', critical:false },
    { id:'c11', text:'Does cutting have its own approved sample for reference?', critical:false },
  ]},
  { key:'production', title:'Section 5: Production Processes', questions:[
    { id:'p1', text:'Is the on-site industrial engineering department adequate and effective?', critical:false },
    { id:'p2', text:'Is there a production planning department?', critical:false },
    { id:'p3', text:'Is there a proper training program for new operators?', critical:false },
    { id:'p4', text:'Are pre-production meetings and pilot runs conducted for each style?', critical:false },
    { id:'p5', text:'Are approved samples available on every line with comments in local language?', critical:false },
    { id:'p6', text:'Are production lines well balanced and set up systematically without bottlenecks?', critical:false },
    { id:'p7', text:'Is there a proper system to record broken/damaged needles and sharp objects?', critical:true },
    { id:'p8', text:'Is production floor well organized, clean and aisles free of clutter?', critical:false },
    { id:'p9', text:'Are adequate systems used to isolate defective and/or repaired products?', critical:true },
    { id:'p10', text:'Are on-site mechanics regularly maintaining all machines with records kept?', critical:false },
    { id:'p11', text:'Is there a proper maintenance department with adequate tools?', critical:false },
    { id:'p12', text:'Are sharp objects (trimmers, scissors) effectively tracked and monitored?', critical:true },
    { id:'p13', text:'Is lighting adequate (100 FC and above)?', critical:false },
  ]},
  { key:'inspection', title:'Section 6: Inspection Processes', questions:[
    { id:'i1', text:'Does factory have inline and end-line inspection with complete garment measurement?', critical:true },
    { id:'i2', text:'Are returned garments from sub-contractors inspected before re-entering bulk production?', critical:false },
    { id:'i3', text:'Does QA/QC have independent reporting structure? Are all QA/QC trained regularly?', critical:true },
    { id:'i4', text:'Does factory have a complete set of technical manuals and knows how to get updates?', critical:false },
    { id:'i5', text:'Does factory conduct daily inspection after sewing line using standard AQL?', critical:false },
    { id:'i6', text:'Is lighting adequate (100 FC and above)?', critical:false },
    { id:'i7', text:'How are alteration/defective garments handled in all sections?', critical:false },
    { id:'i8', text:'Does factory have sufficient, well-equipped inspection room?', critical:false },
  ]},
  { key:'pressing', title:'Section 7: Pressing', questions:[
    { id:'pr1', text:'Is there a pressing supervisor with standards displayed? Sufficient space for garments?', critical:false },
    { id:'pr2', text:'Does factory use appropriate pressing equipment with calibration and maintenance records?', critical:false },
    { id:'pr3', text:'Is there measurement audit after pressing with results properly documented?', critical:false },
    { id:'pr4', text:'Is ventilation and lighting (80 FC) adequate?', critical:false },
    { id:'pr5', text:'Are pressed garments handled properly prior to packing?', critical:false },
  ]},
  { key:'finishing', title:'Section 8: Finishing, Repairing & Packing', questions:[
    { id:'f1', text:'Are packaging standards and samples readily available for reference?', critical:false },
    { id:'f2', text:'Is there 100% final checking after finishing with random audit?', critical:true },
    { id:'f3', text:'Is there an effective stain management and repair operation on site?', critical:false },
    { id:'f4', text:'Is there an organized and efficient work flow in the finishing area?', critical:false },
    { id:'f5', text:'Is lighting adequate (100 FC for inspection, 60-80 for other areas)?', critical:false },
    { id:'f6', text:'Does factory conduct final audit based on standard or tighter AQL?', critical:true },
    { id:'f7', text:'How are alteration/defective garments handled in the section?', critical:false },
    { id:'f8', text:'How is the infrastructure and housekeeping in the section?', critical:false },
    { id:'f9', text:'Is the storage space for packed goods sufficient as per factory capacity?', critical:false },
    { id:'f10', text:'Is there a procedure to remove uncut thread, loose threads and foreign materials?', critical:false },
  ]},
  { key:'safety', title:'Section 9: Product Safety & Satisfaction', questions:[
    { id:'ps1', text:'Does factory have sufficient metal detection process for all products? (Mandatory)', critical:true },
    { id:'ps2', text:'Does factory have pull strength equipment for buttons/snaps/rivets?', critical:true },
    { id:'ps3', text:'Does factory use lockstitch for button attach?', critical:true },
    { id:'ps4', text:'How is Snap/Rivet attaching process and machinery in the factory?', critical:true },
    { id:'ps5', text:'Is there arrangement for disposal of non-conforming goods and materials?', critical:true },
    { id:'ps6', text:'Does factory maintain chemical safety tests for printing items?', critical:true },
  ]},
];

export const scol = s => s && s.includes('Approved') && !s.includes('Prov') ? T.success
  : s && s.includes('Prov') ? T.warning : T.danger;

// ═══════════════════════════════════════════════════════════════════
//  AUDIT TAB
// ═══════════════════════════════════════════════════════════════════
export function AuditTab({ db, setDb, saveToDB, currentUser, suppliers, onCreateCAP }) {
  const audits = db.audits || [];
  const [view, setView] = useState('list');            // list | form | detail
  const [viewAudit, setViewAudit] = useState(null);
  const [activeSection, setActiveSection] = useState(AUDIT_SECTIONS[0].key);
  const [scores, setScores] = useState({});
  const [na, setNa] = useState({});
  const [sectionComments, setSectionComments] = useState({});
  const [meta, setMeta] = useState({ supplierName:'', auditorName:currentUser.name,
    auditDate:today(), auditType:'Full Audit', overallComments:'' });

  const calcSection = (sec) => {
    let earned=0, possible=0, criticalFail=false;
    sec.questions.forEach(q=>{
      if (na[q.id]) return;
      possible += 10;
      const v = parseFloat(scores[q.id]||0);
      earned += v;
      if (q.critical && v === 0 && scores[q.id] !== undefined) criticalFail = true;
    });
    return { earned, possible, pct: possible>0 ? (earned/possible)*100 : 0, criticalFail };
  };
  const calcTotal = () => {
    let te=0, tp=0, anyFail=false;
    AUDIT_SECTIONS.forEach(s=>{ const r=calcSection(s); te+=r.earned; tp+=r.possible; if(r.criticalFail) anyFail=true; });
    const pct = tp>0 ? (te/tp)*100 : 0;
    const grade = anyFail ? 'C' : pct>=80 ? 'A' : pct>=60 ? 'B' : 'C';
    const status = anyFail ? 'Rejected (Critical Fail)' : pct>=80 ? 'Approved'
      : pct>=60 ? 'Provisionally Approved' : 'Rejected';
    return { te, tp, pct, grade, status, anyFail };
  };
  const totals = calcTotal();

  const reset = () => { setScores({}); setNa({}); setSectionComments({});
    setMeta({ supplierName:'', auditorName:currentUser.name, auditDate:today(), auditType:'Full Audit', overallComments:'' }); };

  const handleSave = async () => {
    if (!meta.supplierName) return alert('Select a supplier');
    if (!meta.auditorName)  return alert('Enter auditor name');
    const t = calcTotal();
    const entry = { id: uid(), type:'FACTORY_AUDIT', ...meta, scores:{...scores}, na:{...na},
      sectionComments:{...sectionComments}, totalScore:+t.pct.toFixed(1), earnedPoints:t.te,
      possiblePoints:t.tp, grade:t.grade, status:t.status, capRequired:t.grade!=='A',
      criticalFail:t.anyFail, date:meta.auditDate, approvalStatus:'Pending Approval' };
    setDb(p => ({ ...p,
      audits:[...audits, entry],
      suppliers: (p.suppliers||[]).map(s => s.name===meta.supplierName
        ? { ...s, latestAuditScore:entry.totalScore, latestAuditGrade:entry.grade,
            latestAuditStatus:entry.status, latestAuditDate:entry.date } : s) }));
    await saveToDB('audit', entry);
    reset(); setView('list');
    alert(`Audit saved — ${t.pct.toFixed(1)}% · Grade ${t.grade} · ${t.status}`
      + (entry.capRequired ? '\nCAP is required — raise it from the audit register.' : ''));
  };

  const approveAudit = async (a, decision) => {
    if (!isManager(currentUser)) return alert('Manager rights required to approve an audit result.');
    const upd = { ...a, approvalStatus: decision, approvedBy: currentUser.name, approvedOn: today() };
    setDb(p => ({ ...p, audits: audits.map(x=>x.id===a.id?upd:x) }));
    await saveToDB('audit', upd);
  };

  const printAudit = (a) => printHTML(`Factory Audit — ${a.supplierName}`, `
    <h1>TOQUM · Factory Quality Audit Report</h1>
    <table><tr><td><b>Factory:</b> ${a.supplierName}</td><td><b>Auditor:</b> ${a.auditorName}</td>
      <td><b>Date:</b> ${a.auditDate}</td><td><b>Type:</b> ${a.auditType}</td></tr>
      <tr><td><b>Score:</b> ${a.totalScore}% (${a.earnedPoints}/${a.possiblePoints})</td>
      <td><b>Grade:</b> ${a.grade}</td><td colspan="2"><b>Status:</b> ${a.status}</td></tr></table>
    ${AUDIT_SECTIONS.map(sec=>{
      const qs = sec.questions;
      const earned = qs.filter(q=>!a.na?.[q.id]).reduce((x,q)=>x+(parseFloat(a.scores?.[q.id]||0)),0);
      const possible = qs.filter(q=>!a.na?.[q.id]).length*10;
      return `<h3>${sec.title} — ${possible?((earned/possible)*100).toFixed(0):0}% (${earned}/${possible})</h3>
        <table><tr><th>#</th><th>Question</th><th>Score</th></tr>
        ${qs.map((q,i)=>`<tr><td>${i+1}</td><td>${q.critical?'● ':''}${q.text}</td>
          <td>${a.na?.[q.id]?'N/A':(a.scores?.[q.id]??'—')}</td></tr>`).join('')}</table>
        ${a.sectionComments?.[sec.key]?`<p><b>Comments:</b> ${a.sectionComments[sec.key]}</p>`:''}`;
    }).join('')}
    <p class="note">● Critical question scored 0 = automatic rejection regardless of overall score.</p>
    <table class="sign"><tr><th>Auditor</th><th>Quality Manager</th><th>Factory Representative</th></tr>
      <tr><td>${a.auditorName}</td><td></td><td></td></tr></table>`);

  // ── LIST VIEW ──
  if (view === 'list') {
    const byS = { 'Approved':0, 'Provisionally Approved':0, 'Rejected':0 };
    audits.forEach(a => { if (byS[a.status]!==undefined) byS[a.status]++; else byS['Rejected']++; });
    return (
      <div>
        <Grid cols={5} mb={16}>
          <StatCard label="Total Audits" value={audits.length} color={T.primary} />
          <StatCard label="Approved"     value={byS['Approved']} color={T.success} />
          <StatCard label="Provisional"  value={byS['Provisionally Approved']} color={T.warning} />
          <StatCard label="Rejected"     value={byS['Rejected']} color={T.danger} />
          <StatCard label="CAP Required" value={audits.filter(a=>a.capRequired).length} color={T.purple} />
        </Grid>
        <Section title="Factory Audit Register"
          actions={<Btn small label="+ New Factory Audit" onClick={()=>{ reset(); setActiveSection(AUDIT_SECTIONS[0].key); setView('form'); }} />}>
          <DataTable columns={['Factory','Auditor','Date','Type','Score','Grade','Status','Approval','CAP']}
            rows={audits.slice().reverse()}
            renderCell={(r,c)=>({
              'Factory':<b>{r.supplierName}</b>, 'Auditor':r.auditorName, 'Date':r.auditDate, 'Type':r.auditType,
              'Score':`${r.totalScore}%`,
              'Grade':<b style={{ color:{A:T.success,B:T.warning,C:T.danger}[r.grade] }}>{r.grade}</b>,
              'Status':<span style={{ color:scol(r.status), fontWeight:700, fontSize:11 }}>{r.status}</span>,
              'Approval':<Badge status={r.approvalStatus||'Pending Approval'} />,
              'CAP': r.capRequired
                ? ((db.caps||[]).some(c=>c.auditId===r.id)
                    ? <Tag label="CAP raised" color={T.teal} />
                    : <Btn small color={T.purple} label="Raise CAP" onClick={()=>onCreateCAP(r)} />)
                : <Tag label="Not required" color={T.success} />,
            }[c])}
            actions={r => (
              <span style={{ display:'flex', gap:5 }}>
                <Btn small outline label="View" onClick={()=>{ setViewAudit(r); setView('detail'); }} />
                <Btn small outline label="🖨" onClick={()=>printAudit(r)} />
                {isManager(currentUser) && (r.approvalStatus||'Pending Approval')==='Pending Approval' && <>
                  <Btn small color={T.success} label="Approve" onClick={()=>approveAudit(r,'Approved')} />
                  <Btn small color={T.danger} label="Reject" onClick={()=>approveAudit(r,'Rejected')} />
                </>}
              </span>
            )} />
        </Section>
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (view === 'detail' && viewAudit) {
    const a = viewAudit;
    return (
      <div>
        <Section title={`Audit Report — ${a.supplierName}`}
          actions={<span style={{ display:'flex', gap:6 }}>
            <Btn small outline label="🖨 Print" onClick={()=>printAudit(a)} />
            <Btn small color="#64748b" label="← Back" onClick={()=>setView('list')} /></span>}>
          <Grid cols={4}>
            {[['Auditor',a.auditorName],['Date',a.auditDate],['Type',a.auditType],
              ['Score',`${a.totalScore}% (Grade ${a.grade})`]].map(([l,v])=>(
              <div key={l} style={{ background:'#f8fafc', padding:12, borderRadius:8, border:`1px solid ${T.border}` }}>
                <div style={{ fontSize:10, color:T.textSub, fontWeight:800, textTransform:'uppercase' }}>{l}</div>
                <div style={{ fontWeight:700 }}>{v}</div>
              </div>
            ))}
          </Grid>
          {a.criticalFail && <div style={{ marginTop:10, padding:10, background:'#fef2f2', border:`1px solid ${T.danger}`,
            borderRadius:6, fontSize:12, color:T.danger, fontWeight:700 }}>⚠ Critical question scored 0 — automatic rejection.</div>}
        </Section>
        {AUDIT_SECTIONS.map(sec=>{
          const qs = sec.questions;
          const earned = qs.filter(q=>!a.na?.[q.id]).reduce((x,q)=>x+(parseFloat(a.scores?.[q.id]||0)),0);
          const possible = qs.filter(q=>!a.na?.[q.id]).length*10;
          const pct = possible>0?(earned/possible)*100:0;
          return (
            <div key={sec.key} style={{ marginBottom:12, border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden', background:'white' }}>
              <div style={{ background:'#f8fafc', padding:'10px 16px', borderBottom:`1px solid ${T.border}`,
                display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontWeight:700, fontSize:12 }}>{sec.title}</span>
                <span style={{ fontWeight:800, color: pct>=80?T.success:pct>=60?T.warning:T.danger }}>
                  {pct.toFixed(0)}% ({earned}/{possible})</span>
              </div>
              <div style={{ padding:'10px 16px' }}>
                {qs.map(q=>(
                  <div key={q.id} style={{ display:'flex', gap:10, padding:'5px 0', borderBottom:'1px solid #f8fafc' }}>
                    <div style={{ flex:1, fontSize:11 }}>{q.critical && <span style={{ color:T.danger }}>● </span>}{q.text}</div>
                    <span style={{ fontSize:13, fontWeight:800, minWidth:32, textAlign:'right',
                      color: SCORE_COLORS[a.na?.[q.id]?'NA':(a.scores?.[q.id]||'0')] }}>
                      {a.na?.[q.id]?'N/A':(a.scores?.[q.id]??'—')}</span>
                  </div>
                ))}
                {a.sectionComments?.[sec.key] && (
                  <div style={{ marginTop:8, padding:8, background:'#fffbeb', borderRadius:4, fontSize:11,
                    color:'#92400e', border:'1px solid #fde68a' }}>
                    <b>Comments:</b> {a.sectionComments[sec.key]}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── FORM VIEW ──
  const sec = AUDIT_SECTIONS.find(s=>s.key===activeSection);
  const secResult = calcSection(sec);
  const secIdx = AUDIT_SECTIONS.findIndex(s=>s.key===activeSection);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:20 }}>
      <div>
        <div style={{ background:'white', padding:14, borderRadius:8, border:`1px solid ${T.border}`, marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:700, marginBottom:10, textTransform:'uppercase' }}>Audit Details</div>
          <Field label="Factory *" value={meta.supplierName} onChange={e=>setMeta({...meta,supplierName:e.target.value})}
            options={suppliers.map(s=>s.name)} />
          <Field label="Auditor Name *" value={meta.auditorName} onChange={e=>setMeta({...meta,auditorName:e.target.value})} />
          <Field label="Audit Date" type="date" value={meta.auditDate} onChange={e=>setMeta({...meta,auditDate:e.target.value})} />
          <Field label="Audit Type" value={meta.auditType} onChange={e=>setMeta({...meta,auditType:e.target.value})}
            options={['Full Audit','Follow-up Audit','Pre-shipment Check','CAP Verification']} />
        </div>
        <div style={{ background:'white', borderRadius:8, border:`1px solid ${T.border}`, overflow:'hidden', marginBottom:12 }}>
          {AUDIT_SECTIONS.map(s=>{
            const r = calcSection(s);
            const answered = s.questions.filter(q=>scores[q.id]!==undefined||na[q.id]).length;
            const act = activeSection===s.key;
            return (
              <div key={s.key} onClick={()=>setActiveSection(s.key)}
                style={{ padding:'8px 12px', cursor:'pointer', background: act?'#1f1f1f':'white', borderBottom:'1px solid #f1f5f9' }}>
                <div style={{ fontSize:10, fontWeight:700, color: act?'white':T.textMain, marginBottom:4 }}>{s.title.split(':')[0]}</div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <div style={{ flex:1, height:3, background: act?'rgba(255,255,255,0.2)':'#f1f5f9', borderRadius:2 }}>
                    <div style={{ height:'100%', width:`${r.pct}%`, borderRadius:2,
                      background: r.pct>=80?T.success:r.pct>=60?T.warning:T.danger }} />
                  </div>
                  <span style={{ fontSize:9, color: act?'#93c5fd':T.textSub }}>{answered}/{s.questions.length}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ background: totals.grade==='A'?'#f0fdf4':totals.grade==='B'?'#fef3c7':'#fef2f2',
          padding:12, borderRadius:8, border:`2px solid ${scol(totals.status)}` }}>
          <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:'uppercase', marginBottom:6 }}>Running Score</div>
          <div style={{ fontSize:28, fontWeight:900, color:scol(totals.status), lineHeight:1 }}>{totals.pct.toFixed(1)}%</div>
          <div style={{ fontSize:11, color:T.textSub, margin:'4px 0' }}>{totals.te}/{totals.tp} pts</div>
          <div style={{ padding:'3px 8px', background:`${scol(totals.status)}15`, color:scol(totals.status),
            borderRadius:8, fontSize:10, fontWeight:800, display:'inline-block', marginBottom:10 }}>Grade {totals.grade}</div>
          {totals.anyFail && <div style={{ fontSize:10, color:T.danger, background:'#fef2f2', padding:'5px 8px',
            borderRadius:4, marginBottom:8 }}>⚠ Critical question scored 0</div>}
          {totals.grade!=='A' && <div style={{ fontSize:10, color:T.purple, background:'#f5f3ff', padding:'5px 8px',
            borderRadius:4, marginBottom:8 }}>CAP will be required on save</div>}
          <Btn label="💾 Save Audit" color={T.success} onClick={handleSave} />
          <div style={{ marginTop:6 }}>
            <Btn label="Cancel" outline color="#64748b" onClick={()=>{ reset(); setView('list'); }} />
          </div>
        </div>
      </div>

      <div>
        <div style={{ background:'white', borderRadius:10, border:`1px solid ${T.border}`, overflow:'hidden' }}>
          <div style={{ background:'#1f1f1f', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontWeight:800, color:'white', fontSize:14 }}>{sec.title}</div>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                10 = Excellent · 5 = Acceptable · 3 = Partial · 0 = Unacceptable · N/A
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:22, fontWeight:900,
                color: secResult.pct>=80?'#4ade80':secResult.pct>=60?'#fbbf24':'#f87171' }}>{secResult.pct.toFixed(0)}%</div>
              <div style={{ fontSize:10, color:'#94a3b8' }}>{secResult.earned}/{secResult.possible}</div>
            </div>
          </div>
          {sec.questions.map((q,qi)=>{
            const isNA = na[q.id]||false;
            const score = scores[q.id];
            const sc = SCORE_COLORS[score]||'#94a3b8';
            return (
              <div key={q.id} style={{ padding:'12px 20px', borderBottom:'1px solid #f8fafc',
                background: isNA?'#f8fafc': (score==='0'&&q.critical)?'#fff8f8':'white' }}>
                <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:'#f1f5f9', display:'flex',
                    alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:T.textSub, flexShrink:0, marginTop:2 }}>{qi+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, lineHeight:1.5, marginBottom:8 }}>
                      {q.critical && <span style={{ color:T.danger, marginRight:4 }}>●</span>}{q.text}
                      {q.critical && <Tag label="CRITICAL" color={T.danger} />}
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {[10,5,3,0].map(v=>(
                        <button key={v} disabled={isNA} onClick={()=>!isNA && setScores(p=>({...p,[q.id]:String(v)}))}
                          style={{ padding:'5px 12px', borderRadius:6, fontSize:12, cursor:isNA?'not-allowed':'pointer',
                            border:`2px solid ${score===String(v)?SCORE_COLORS[String(v)]:'#e2e8f0'}`,
                            background: score===String(v)?`${SCORE_COLORS[String(v)]}20`:'white',
                            color: score===String(v)?SCORE_COLORS[String(v)]:T.textSub,
                            fontWeight: score===String(v)?800:500, opacity:isNA?0.4:1 }}>{v}</button>
                      ))}
                      <button onClick={()=>{ setNa(p=>({...p,[q.id]:!isNA})); if(!isNA) setScores(p=>{ const n={...p}; delete n[q.id]; return n; }); }}
                        style={{ padding:'5px 10px', borderRadius:6, fontSize:11, cursor:'pointer',
                          border:`2px solid ${isNA?'#64748b':'#e2e8f0'}`, background:isNA?'#64748b20':'white',
                          color:isNA?'#64748b':'#94a3b8', fontWeight:isNA?800:500 }}>N/A</button>
                      {score!==undefined && !isNA && (
                        <button onClick={()=>setScores(p=>{ const n={...p}; delete n[q.id]; return n; })}
                          style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #fecaca', background:'none',
                            color:'#ef4444', cursor:'pointer', fontSize:10 }}>Clear</button>
                      )}
                    </div>
                  </div>
                  {score!==undefined && !isNA && (
                    <div style={{ width:34, height:34, borderRadius:8, background:`${sc}20`, border:`2px solid ${sc}`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:900, color:sc, flexShrink:0 }}>{score}</div>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ padding:'14px 20px', background:'#f8fafc', borderTop:`1px solid ${T.border}` }}>
            <label style={{ fontSize:11, fontWeight:700, display:'block', marginBottom:6 }}>
              Section Comments / CAP Notes
              {secResult.criticalFail && <span style={{ marginLeft:8, color:T.danger, fontSize:10 }}>⚠ CAP required</span>}
            </label>
            <textarea value={sectionComments[sec.key]||''} onChange={e=>setSectionComments(p=>({...p,[sec.key]:e.target.value}))}
              placeholder="Enter observations, findings and corrective action required..."
              style={{ width:'100%', padding:8, border:'1px solid #cbd5e1', borderRadius:4, fontSize:12,
                height:60, resize:'vertical', boxSizing:'border-box' }} />
          </div>
          <div style={{ padding:'10px 20px', display:'flex', justifyContent:'space-between', borderTop:`1px solid ${T.border}` }}>
            <button disabled={secIdx===0} onClick={()=>secIdx>0 && setActiveSection(AUDIT_SECTIONS[secIdx-1].key)}
              style={{ padding:'7px 14px', background:'#f1f5f9', border:`1px solid ${T.border}`, borderRadius:6,
                cursor:'pointer', fontWeight:600, fontSize:12, opacity:secIdx===0?0.4:1 }}>← Previous</button>
            <button disabled={secIdx===AUDIT_SECTIONS.length-1}
              onClick={()=>secIdx<AUDIT_SECTIONS.length-1 && setActiveSection(AUDIT_SECTIONS[secIdx+1].key)}
              style={{ padding:'7px 14px', background:'#1f1f1f', color:'white', border:'none', borderRadius:6,
                cursor:'pointer', fontWeight:600, fontSize:12, opacity:secIdx===AUDIT_SECTIONS.length-1?0.4:1 }}>Next →</button>
          </div>
        </div>
        <div style={{ marginTop:12, padding:12, background:'white', borderRadius:8, border:`1px solid ${T.border}` }}>
          <div style={{ fontWeight:700, fontSize:11, marginBottom:8, textTransform:'uppercase' }}>Rating Guide</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
            {[{r:'80–100% (A)',l:'Approved',c:T.success,bg:'#f0fdf4'},
              {r:'60–79% (B)',l:'Provisionally Approved',c:T.warning,bg:'#fef3c7'},
              {r:'0–59% (C)',l:'Rejected',c:T.danger,bg:'#fef2f2'}].map(g=>(
              <div key={g.r} style={{ padding:8, background:g.bg, borderRadius:6, border:`1px solid ${g.c}30` }}>
                <div style={{ fontWeight:800, color:g.c, fontSize:11 }}>{g.r}</div>
                <div style={{ fontWeight:700, color:g.c, fontSize:10 }}>{g.l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:8, fontSize:10, color:T.textSub }}>
            ● Critical questions scored 0 = automatic rejection regardless of overall score.
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  CAP TAB — raise · respond · verify · approve · close
// ═══════════════════════════════════════════════════════════════════
export function CapTab({ db, setDb, saveToDB, currentUser, suppliers, seedAudit, clearSeed }) {
  const caps = db.caps || [];
  const audits = db.audits || [];
  const [creating, setCreating] = useState(seedAudit || null);
  const [detail, setDetail] = useState(null);

  React.useEffect(()=>{ if (seedAudit) setCreating(seedAudit); }, [seedAudit]);

  // Pre-fill findings from audit: every scored 0/3 question + section comments
  const findingsFromAudit = (a) => {
    if (!a) return [];
    const out = [];
    AUDIT_SECTIONS.forEach(sec => {
      sec.questions.forEach(q => {
        const sc = a.scores?.[q.id];
        if (sc === '0' || sc === '3') {
          out.push({ section: sec.title.split(':')[1]?.trim() || sec.title,
            finding: q.text, severity: q.critical ? 'Critical' : (sc==='0' ? 'Major' : 'Minor'),
            rootCause:'', action:'', responsible:'', dueDate: addDays(today(), q.critical ? 7 : 21), status:'Open' });
        }
      });
      if (a.sectionComments?.[sec.key]) {
        out.push({ section: sec.title.split(':')[1]?.trim() || sec.title,
          finding: a.sectionComments[sec.key], severity:'Observation', rootCause:'', action:'',
          responsible:'', dueDate: addDays(today(), 21), status:'Open' });
      }
    });
    return out;
  };

  const CapCreator = ({ audit }) => {
    const [supplier, setSupplier] = useState(audit?.supplierName || '');
    const [items, setItems] = useState(findingsFromAudit(audit));
    const [meta, setMeta] = useState({ title: audit ? `CAP — ${audit.auditType} ${audit.auditDate}` : 'CAP',
      issuedTo:'', targetClosure: addDays(today(), 30) });

    const save = async () => {
      if (!supplier) return alert('Select a supplier');
      if (!items.length) return alert('Add at least one finding');
      const entry = { id: uid(), auditId: audit?.id || null, supplier, ...meta,
        createdBy: currentUser.name, createdOn: today(), items,
        status:'Issued', approvalStatus:'Pending Approval', progress:0,
        log:[{ date:today(), event:'CAP issued to supplier', by:currentUser.name }] };
      setDb(p=>({ ...p, caps:[...caps, entry] }));
      await saveToDB('cap', entry);
      setCreating(null); clearSeed && clearSeed();
      alert('CAP issued. Track responses and verification in the CAP register.');
    };

    return (
      <Section title={`Generate CAP${audit?` — from audit of ${audit.supplierName} (Grade ${audit.grade}, ${audit.totalScore}%)`:''}`}
        actions={<Btn small color="#64748b" label="Close" onClick={()=>{ setCreating(null); clearSeed && clearSeed(); }} />}>
        <Grid cols={4}>
          <Field label="Supplier" value={supplier} onChange={e=>setSupplier(e.target.value)} options={suppliers.map(s=>s.name)} />
          <Field label="CAP Title" value={meta.title} onChange={e=>setMeta({...meta,title:e.target.value})} />
          <Field label="Issued To (factory contact)" value={meta.issuedTo} onChange={e=>setMeta({...meta,issuedTo:e.target.value})} />
          <Field label="Target Closure Date" type="date" value={meta.targetClosure} onChange={e=>setMeta({...meta,targetClosure:e.target.value})} />
        </Grid>
        {audit && <div style={{ fontSize:11.5, color:T.textSub, background:'#f8fafc', padding:8, borderRadius:6, marginBottom:10 }}>
          {items.length} finding(s) pre-loaded from every question scored 0 or 3, plus your section comments. Edit or add rows as needed.
        </div>}
        <DynamicTable dense
          columns={[
            { label:'Section', key:'section', width:'120px' },
            { label:'Finding / Non-Conformity', key:'finding', type:'textarea', width:'240px' },
            { label:'Severity', key:'severity', type:'select', options:['Critical','Major','Minor','Observation'], width:'100px' },
            { label:'Root Cause', key:'rootCause', type:'textarea', width:'160px' },
            { label:'Corrective Action Required', key:'action', type:'textarea', width:'200px' },
            { label:'Responsible', key:'responsible', width:'110px' },
            { label:'Due Date', key:'dueDate', type:'date', width:'110px' },
          ]}
          data={items} setData={setItems}
          onAdd={()=>setItems([...items,{ severity:'Major', dueDate:addDays(today(),21), status:'Open' }])} />
        <Btn label="Issue CAP to Supplier" color={T.success} onClick={save} />
      </Section>
    );
  };

  const recalc = (items) => {
    const closed = items.filter(i=>i.status==='Verified Closed').length;
    return items.length ? Math.round(closed/items.length*100) : 0;
  };

  const updateCap = async (cap, patch, event) => {
    const upd = { ...cap, ...patch, log:[...(cap.log||[]), { date:today(), event, by:currentUser.name }] };
    setDb(p=>({ ...p, caps: caps.map(c=>c.id===cap.id?upd:c) }));
    await saveToDB('cap', upd);
    setDetail(upd);
  };

  const CapDetail = ({ cap }) => {
    const setItem = (idx, k, v) => {
      const items = cap.items.map((it,i)=> i===idx ? { ...it, [k]:v } : it);
      const progress = recalc(items);
      updateCap(cap, { items, progress,
        status: progress===100 ? 'Closed — Pending Approval' : progress>0 ? 'In Progress' : cap.status },
        `Item ${idx+1} updated (${k})`);
    };
    const overdue = cap.items.filter(i=>i.status!=='Verified Closed' && i.dueDate < today()).length;

    const printCap = () => printHTML(`CAP — ${cap.supplier}`, `
      <h1>TOQUM · Corrective Action Plan</h1>
      <table><tr><td><b>Supplier:</b> ${cap.supplier}</td><td><b>CAP No:</b> CAP-${String(cap.id).slice(-5)}</td>
        <td><b>Issued:</b> ${cap.createdOn}</td><td><b>Target Closure:</b> ${cap.targetClosure}</td></tr>
        <tr><td><b>Issued By:</b> ${cap.createdBy}</td><td><b>Issued To:</b> ${cap.issuedTo||''}</td>
        <td><b>Status:</b> ${cap.status}</td><td><b>Progress:</b> ${cap.progress}%</td></tr></table>
      <table><tr><th>#</th><th>Section</th><th>Finding</th><th>Severity</th><th>Root Cause</th>
        <th>Corrective Action</th><th>Supplier Response</th><th>Responsible</th><th>Due</th><th>Status</th></tr>
        ${cap.items.map((it,i)=>`<tr><td>${i+1}</td><td>${it.section||''}</td><td>${it.finding||''}</td>
          <td>${it.severity||''}</td><td>${it.rootCause||''}</td><td>${it.action||''}</td>
          <td>${it.supplierResponse||''}</td><td>${it.responsible||''}</td><td>${it.dueDate||''}</td>
          <td>${it.status||'Open'}</td></tr>`).join('')}</table>
      <table class="sign"><tr><th>Issued By (QA)</th><th>Factory Representative</th><th>Verified By</th><th>Approved By (QA Manager)</th></tr>
        <tr><td>${cap.createdBy}</td><td>${cap.issuedTo||''}</td><td>${cap.verifiedBy||''}</td><td>${cap.approvedBy||''}</td></tr></table>`);

    return (
      <Section title={`CAP-${String(cap.id).slice(-5)} — ${cap.supplier}`}
        actions={<span style={{ display:'flex', gap:6 }}>
          <Btn small outline label="🖨 Print" onClick={printCap} />
          <Btn small color="#64748b" label="← Back" onClick={()=>setDetail(null)} /></span>}>
        <Grid cols={5} mb={12}>
          <StatCard label="Status" value={cap.status} color={T.warning} />
          <StatCard label="Progress" value={`${cap.progress}%`} color={cap.progress===100?T.success:T.warning} />
          <StatCard label="Findings" value={cap.items.length} color={T.primary} />
          <StatCard label="Overdue" value={overdue} color={overdue?T.danger:T.success} />
          <StatCard label="Approval" value={cap.approvalStatus||'Pending Approval'} color={T.purple} />
        </Grid>
        <div style={{ height:8, background:'#f1f5f9', borderRadius:4, marginBottom:14 }}>
          <div style={{ height:8, width:`${cap.progress}%`, borderRadius:4,
            background: cap.progress===100?T.success:T.warning }} />
        </div>

        {cap.items.map((it,i)=>(
          <Collapse key={i} defaultOpen={it.status!=='Verified Closed'}
            title={`${i+1}. ${it.section||''} — ${(it.finding||'').slice(0,70)}${(it.finding||'').length>70?'…':''}`}
            badge={`${it.severity} · ${it.status||'Open'}`}
            color={{ Critical:T.danger, Major:T.warning, Minor:T.primary, Observation:T.textSub }[it.severity]||T.primary}>
            <div style={{ fontSize:12, marginBottom:8 }}><b>Finding:</b> {it.finding}</div>
            <Grid cols={2}>
              <Field label="Root Cause (supplier)" type="textarea" value={it.rootCause||''} onChange={e=>setItem(i,'rootCause',e.target.value)} />
              <Field label="Corrective Action Required" type="textarea" value={it.action||''} onChange={e=>setItem(i,'action',e.target.value)} />
              <Field label="Supplier Response / Evidence" type="textarea" value={it.supplierResponse||''} onChange={e=>setItem(i,'supplierResponse',e.target.value)} />
              <Field label="Preventive Action (to stop recurrence)" type="textarea" value={it.preventive||''} onChange={e=>setItem(i,'preventive',e.target.value)} />
              <Field label="Responsible" value={it.responsible||''} onChange={e=>setItem(i,'responsible',e.target.value)} />
              <Field label="Due Date" type="date" value={it.dueDate||''} onChange={e=>setItem(i,'dueDate',e.target.value)} />
              <Field label="Status" value={it.status||'Open'} onChange={e=>setItem(i,'status',e.target.value)}
                options={['Open','Supplier Responded','Under Verification','Verified Closed','Rejected — Redo']} />
              <Field label="Verification Note (QA)" type="textarea" value={it.verificationNote||''} onChange={e=>setItem(i,'verificationNote',e.target.value)} />
            </Grid>
            {it.status!=='Verified Closed' && it.dueDate < today() &&
              <div style={{ color:T.danger, fontSize:11, fontWeight:700 }}>⚠ Overdue since {it.dueDate}</div>}
          </Collapse>
        ))}

        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:12 }}>
          {cap.progress===100 && (cap.approvalStatus||'Pending Approval')==='Pending Approval' && isManager(currentUser) && (
            <>
              <Btn label="Approve & Close CAP" color={T.success}
                onClick={()=>updateCap(cap,{ approvalStatus:'Approved', status:'Closed',
                  approvedBy:currentUser.name, closedOn:today() }, 'CAP approved and closed by manager')} />
              <Btn label="Reject — Send Back" color={T.danger}
                onClick={()=>updateCap(cap,{ approvalStatus:'Rejected', status:'In Progress' },
                  'CAP rejected by manager — supplier must redo')} />
            </>
          )}
          <Btn label="Schedule Verification Audit" outline color={T.purple}
            onClick={()=>updateCap(cap,{ verificationScheduled: addDays(today(),7), verifiedBy:currentUser.name },
              'Verification audit scheduled')} />
        </div>

        <div style={{ marginTop:14 }}>
          <b style={{ fontSize:12 }}>CAP Log</b>
          {(cap.log||[]).map((l,i)=>(
            <div key={i} style={{ fontSize:11.5, padding:'3px 0 3px 10px', borderLeft:`3px solid ${T.primary}`, marginTop:4 }}>
              <b>{l.date}</b> — {l.event} <span style={{ color:T.textSub }}>({l.by})</span>
            </div>
          ))}
        </div>
      </Section>
    );
  };

  if (creating) return <CapCreator audit={creating.id ? creating : null} />;
  if (detail)   return <CapDetail cap={caps.find(c=>c.id===detail.id)||detail} />;

  const openCaps = caps.filter(c=>c.status!=='Closed');
  const overdueCaps = caps.filter(c=>c.items?.some(i=>i.status!=='Verified Closed' && i.dueDate<today()));
  return (
    <div>
      <Grid cols={5} mb={16}>
        <StatCard label="Total CAPs" value={caps.length} color={T.primary} />
        <StatCard label="Open" value={openCaps.length} color={T.warning} />
        <StatCard label="Overdue" value={overdueCaps.length} color={T.danger} />
        <StatCard label="Closed" value={caps.filter(c=>c.status==='Closed').length} color={T.success} />
        <StatCard label="Awaiting Approval"
          value={caps.filter(c=>c.progress===100 && (c.approvalStatus||'Pending Approval')==='Pending Approval').length}
          color={T.purple} />
      </Grid>
      <Section title="CAP Register — issue, follow up, verify, approve"
        actions={<Btn small label="+ Raise CAP (standalone)" onClick={()=>setCreating({})} />}>
        <DataTable columns={['CAP No','Supplier','Issued','Target','Findings','Progress','Status','Approval']}
          rows={caps.slice().reverse()}
          renderCell={(r,c)=>({
            'CAP No':<b>CAP-{String(r.id).slice(-5)}</b>, 'Supplier':r.supplier, 'Issued':r.createdOn,
            'Target':<span style={{ color: r.status!=='Closed'&&r.targetClosure<today()?T.danger:'inherit' }}>{r.targetClosure}</span>,
            'Findings':r.items?.length||0,
            'Progress':<span style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:50, height:5, background:'#f1f5f9', borderRadius:3, display:'inline-block' }}>
                <span style={{ display:'block', height:5, width:`${r.progress}%`, borderRadius:3,
                  background:r.progress===100?T.success:T.warning }} /></span>
              <b>{r.progress}%</b></span>,
            'Status':<Badge status={r.status==='Closed'?'Closed':r.progress>0?'In Progress':'Open'} />,
            'Approval':<Badge status={r.approvalStatus||'Pending Approval'} />,
          }[c])}
          actions={r => <Btn small outline label="Open" onClick={()=>setDetail(r)} />}
          emptyMsg="No CAPs yet — raise one from a graded audit or standalone." />
      </Section>
      {audits.filter(a=>a.capRequired && !caps.some(c=>c.auditId===a.id)).length>0 && (
        <Section title="⚠ Audits requiring a CAP">
          <DataTable columns={['Factory','Date','Score','Grade','Status']}
            rows={audits.filter(a=>a.capRequired && !caps.some(c=>c.auditId===a.id))}
            renderCell={(r,c)=>({ 'Factory':r.supplierName, 'Date':r.auditDate, 'Score':`${r.totalScore}%`,
              'Grade':r.grade, 'Status':r.status }[c])}
            actions={r => <Btn small color={T.purple} label="Raise CAP" onClick={()=>setCreating(r)} />} />
        </Section>
      )}
    </div>
  );
}
